import Validation from "./Validation/Validation";
import type { IValidationErrorDto, IValidatorDto, IValidatorResult } from "@/types/FormEngine/validation";
import { isControl, isOptionBased, isTreeSection } from "@/helpers/Misc";
import Signer from "./DSigner/Signer";

export default class FormController {
  public rootElement: HTMLElement | null;
  public isValid: boolean = true;
  public controls: Map<String, any> = new Map();
  public sections: Map<String, any> = new Map();
  // Registry of all overlays
  public overlays: Map<String, any> = new Map();
  // For multiple screens open On top of each other
  public activeOverlayStack: Map<string, any> = new Map();
  // Currently active overlays 
  public activeOverlay: any = null;

  public xsdResults = [];

  public validation = Validation;

  public errorBag: any = null;
  public wizard: any = null;
  public app: any = null;

  public rules: Dictionary<Function> = {};

  public spinnerActive: boolean = false;

  constructor(rootElementIdSelector: string) {
    this.rootElement = document.getElementById(rootElementIdSelector);
    this.validation.setValidatorResolverHash();
    return this;
  }
  public registerApp(app: any) {
    this.app = app;
  }
  public toggleReadOnly() {
    this.app.readOnly = !this.app.readOnly;
  }
  public setReadOnly(toValue: boolean) {
    this.app.readOnly = toValue;
  }
  /**
   * Utility
   */
  public registerControl(identifier: string, control: any) {
    if (control.defaultState.required) {
      this.validation.setValidatorFromLib("required", { control: control });
      if (control.mtdt.LDT.MinLength) {
        this.validation.setValidatorFromLib("minLength", { control: control });
      }
    }
    if (control.mtdt.IsTable) {
      this.validation.setValidatorFromLib("tableRows", { control: control });
    }
    if (control.mtdt.LDT.MinRows && control.mtdt.IsTable) {
      this.validation.setValidatorFromLib("minRows", { control: control });
    }
    if (control.mtdt.LDT.MaxRows && control.mtdt.IsTable) {
      this.validation.setValidatorFromLib("maxRows", { control: control });
    }
    if (control.mtdt.LDT.MinDate && control.mtdt.LDT.FieldType === "Date") {
      this.validation.setValidatorFromLib("minDate", { control: control });
    }
    if (control.mtdt.LDT.MaxDate && control.mtdt.LDT.FieldType === "Date") {
      this.validation.setValidatorFromLib("maxDate", { control: control });
    }
    if (control.mtdt.LDT.MinCount && control.mtdt.LDT.FieldType === "TextBox") {
      this.validation.setValidatorFromLib("minCount", { control: control });
    }
    if (control.mtdt.LDT.MaxCount && control.mtdt.LDT.FieldType === "TextBox") {
      this.validation.setValidatorFromLib("maxCount", { control: control });
    }
    if (control.mtdt.LDT.RegularExpression) {
      this.validation.setValidatorFromLib("regex", { control: control });
    } else {
      if (control.mtdt.LDT.MaxLength) {
        this.validation.setValidatorFromLib("maxLength", { control: control });
      }
    }

    this.controls.set(identifier, control);
  }
  public registerSection(identifier: string, section: any) {
    window.FormEngine.events.subscribe(
      window.FormEngine.events.list.SECTION_CLOSE,
      window.FormEngine.events.eventFunctionsLibrary.sectionClose,
      section.identifier
    );
    this.sections.set(identifier, section);
  }
  public registerOverlay(identifier: string, overlay: any) {
    this.overlays.set(identifier, overlay);
  }
  public registerFunctionality() { }
  /**
   * Error bag
   */
  public registerErrorBag(errorBag: any) {
    this.errorBag = errorBag;
  }
  /**
   * Error bag
   */
  public registerWizard(wizard: any) {
    this.wizard = wizard;
  }
  /**
   * Control manipulation
   */

  public getTargetable(elementId: string) {
    let temp = this.controls.get(elementId);
    if (!temp) {
      temp = this.sections.get(elementId);
    }
    return temp;
  }
  public getControls(element: Array<HTMLElement> | Array<string> | HTMLElement | string | null = null): Array<any> {
    let fc = this;
    let controls = new Array<any>();
    if (element instanceof Array) {
      element.forEach((el) => {
        controls = [...controls, ...fc.getControls(el)];
      });
    } else if (element instanceof HTMLElement) {
      let isControl = fc.controls.get(element.id);
      if (isControl) {
        controls.push(isControl);
      } else {
        let elementsWithIds: Array<HTMLElement> = Array.from(element.querySelectorAll("[id]")) as Array<HTMLElement>;
        elementsWithIds.forEach((el) => {
          let temp = fc.controls.get(el.id);
          if (temp) {
            controls.push(temp);
          }
        });
      }
    } else if (typeof element === "string") {
      let tempElem = document.getElementById(element);
      return fc.getControls(tempElem);
    } else {
      return Array.from(this.controls.values());
    }
    return controls;
  }

  public getSections(element: Array<HTMLElement> | Array<string> | HTMLElement | string | null = null): Array<any> {
    let fc = this;
    let sections = new Array<any>();
    if (element instanceof Array) {
      element.forEach((el) => {
        sections = [...sections, ...fc.getSections(el)];
      });
    } else if (element instanceof HTMLElement) {
      let isControl = fc.sections.get(element.id);
      if (isControl) {
        sections.push(isControl);
      } else {
        let elementsWithIds: Array<HTMLElement> = Array.from(element.querySelectorAll("[id]")) as Array<HTMLElement>;
        elementsWithIds.forEach((el) => {
          let temp = fc.sections.get(el.id);
          if (temp) {
            sections.push(temp);
          }
        });
      }
    } else if (typeof element === "string") {
      let tempElem = document.getElementById(element);
      return fc.getSections(tempElem);
    } else {
      return Array.from(this.sections.values());
    }
    return sections;

    //return Array.from(this.sections.values());
  }

  public isValidControl(controlId: string) {
    return this.errorBag.find((error: IValidationErrorDto) => error.targetId === controlId);
  }

  public hide(element: Array<HTMLElement> | string | HTMLElement) {
    let controls = this.getControls(element);
    let sections = this.getSections(element);
    controls.forEach((control) => {
      control.hide();
    });
    sections.forEach((section) => {
      section.hide();
    });
  }

  public show(element: Array<HTMLElement> | Array<string> | string | HTMLElement) {
    let controls = this.getControls(element);
    let sections = this.getSections(element);
    controls.forEach((control) => {
      control.show();
    });
    sections.forEach((section) => {
      section.show();
    });
  }

  public setToExport(element: Array<HTMLElement> | string | HTMLElement, toExport: boolean = true) {
    let controls = this.getControls(element);
    controls.forEach((control) => {
      control.setToExport(toExport);
    });
  }
  public reset() {
    let controls = this.getControls().filter((c) => {
      return isControl(c.mtdt) && !c.config.IsTableDetailElement;
    });
    for (let control of controls) {
      control.reset();
    }
    this.errorBag.resetErrors();
  }
  public setLoading(isLoading = true) {
    this.spinnerActive = isLoading;
  }
  public showMessage(message: string) { }

  public navigateToTarget(controlId: string) {
    let control = window.FormEngine.form.controls.get(controlId);
    if (control) {
      let navigationData = control.getNavigationData();
      if (navigationData) {
        for (let navStep of navigationData) {
          let tempC = window.FormEngine.form.sections.get(navStep.identifier);
          if (!tempC) {
            tempC = window.FormEngine.form.controls.get(navStep.identifier);
            if (tempC.mtdt.IsTable) {
              //console.log(navStep);
              tempC.openDetailViewRowNumber(navStep.openedRow);
            } else {
              let parentTarget = window.FormEngine.form.getTargetable(tempC.mtdt.Parent);
              if (isTreeSection(parentTarget.mtdt)) {
                parentTarget.openAccordionTab();
              }
            }
          } else {
            tempC.openAccordionTab();
          }
        }
      }
    }
  }
  /**
   * Modal
   */
  public openModal() { }
  public closeModal() { }
  public async getFormPreviewAsync() { }
  /**
   * Validation
   */
  public validate() {
    this.validation.validateAll();
  }
  public validateWithXsd() {
    let content = {
      xml: window.FormEngine.xmlService.exportXmlAsString() as string,
      xsd: Signer.b64DecodeUnicode(window.FormEngine.structure.structureJSON.xsdBase64)
    };
    let self = this;
    let checkUrl = (window.FormEngine.engineConfig.xmlToXsdValidatorUrl) ? window.FormEngine.engineConfig.xmlToXsdValidatorUrl : "http://dev-filler:8080/api/validate/xmlWithXsd";
    fetch(checkUrl, {
      method: 'POST',
      body: JSON.stringify(content),
      headers: new Headers({ 'Content-Type': 'application/json' }),
      //mode: "no-cors"
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error("Validate xsd has thrown error");
      }
      return response.text();
    }).then((data) => {
      try {
        let d = JSON.parse(data);
        self.xsdResults = d;
        window.FormEngine.form.sections.get("_XmlXsltValidationProcessOverlayWindowList").state.additional.xsdResults = d;
        window.FormEngine.form.overlays.get("_XmlXsltValidationProcessOverlayWindow").show();
      } catch (error) {
        console.error("JSON Parse error:", error);
      }
    })
      .catch((error) => {
        console.error("Validate xsd failed: ", error);
      });

  }

  // INTETIONALY NOT STATIC SO IT CAN BE CALLED ONLY AFTER FORMCONTROLLER WAS INITIALIZED - RELIES ON form being already assembled and displayed (so it can check if control/section exists);
  public walkTreeWith(
    tree: ITree,
    functionIfControl: Function,
    functionIfSection?: Function | null,
    walkDeeperCondition: Function = (element: any) => {
      return true;
    }
  ) {
    for (let elementName in tree) {
      let element = window.FormEngine.form.controls.get(elementName);
      if (!element) {
        element = window.FormEngine.form.sections.get(elementName);
        if (element && functionIfSection) {
          functionIfSection(element);
        }
      } else {
        functionIfControl(element);
      }
      //console.log(elementName,element);
      if (element && element.mtdt.Tree && !element.mtdt.IsTable) {
        //console.log("ELEMENT HAS TREE:", elementName);
        if (walkDeeperCondition && walkDeeperCondition(element)) {
          this.walkTreeWith(element.mtdt.Tree, functionIfControl, functionIfSection, walkDeeperCondition);
        }
      }
    }
  }
  /**
   * XML Functions
   */
  public setValuesFromXml(xml: Element, silent: boolean = true) {
    let controlsArray = this.getControls().filter(
      (control) =>
        isControl(control.mtdt) && !control.mtdt.IsTableDetailElement && (control.config.xmlNodeSelector || control.config.xmlNodeCodeSelector)
    );
    console.log("setValuesFromXml => Silent run:" + silent, xml);
    for (let control of controlsArray) {
      control.handleXmlImport(xml, silent);
    }
  }
}
